'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, AlertTriangle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface RegisterFormData {
  fullName: string;
  email: string;
  mobile: string;
  pan: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
  riskDisclosureAccepted: boolean;
}

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score: 1, label: 'Weak', color: '#EF4444' };
  if (score <= 2) return { score: 2, label: 'Fair', color: '#F59E0B' };
  if (score <= 3) return { score: 3, label: 'Good', color: '#3B82F6' };
  return { score: 4, label: 'Strong', color: '#10B981' };
}

export default function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { signUp } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    defaultValues: {
      fullName: '',
      email: '',
      mobile: '',
      pan: '',
      password: '',
      confirmPassword: '',
      termsAccepted: false,
      riskDisclosureAccepted: false,
    },
  });

  const password = watch('password', '');
  const strength = getPasswordStrength(password);

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        role: 'investor',
        kyc_status: 'not_started',
      });
      toast.success('Account created! Redirecting to your dashboard…');
      router.push('/investor-home-dashboard');
    } catch (err: any) {
      setIsLoading(false);
      toast.error(err?.message ?? 'Registration failed. Please try again.');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-700 text-foreground">Create your account</h2>
        <p className="text-sm text-muted-foreground mt-1">Join ZAX-CORP — complete KYC after registration</p>
      </div>

      {/* Compliance notice */}
      <div className="flex gap-2.5 rounded-xl p-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
        <AlertTriangle size={14} className="text-warning flex-shrink-0 mt-0.5" />
        <p className="text-xs text-warning leading-relaxed">
          ZAX-CORP serves eligible users subject to Indian VDA regulations. KYC, PAN verification and AML screening are mandatory before investing.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Full Name */}
        <div>
          <label htmlFor="fullName" className="block text-xs font-600 text-muted-foreground mb-1.5 tracking-wide uppercase">
            Full Name <span className="text-danger">*</span>
          </label>
          <input
            id="fullName"
            type="text"
            className="input-field"
            placeholder="As per PAN card"
            autoComplete="name"
            {...register('fullName', {
              required: 'Full name is required',
              minLength: { value: 3, message: 'Name must be at least 3 characters' },
              pattern: { value: /^[a-zA-Z\s]+$/, message: 'Name should contain only letters' },
            })}
          />
          {errors.fullName && (
            <p className="mt-1 text-xs text-danger" role="alert">{errors.fullName.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="reg-email" className="block text-xs font-600 text-muted-foreground mb-1.5 tracking-wide uppercase">
            Email Address <span className="text-danger">*</span>
          </label>
          <input
            id="reg-email"
            type="email"
            className="input-field"
            placeholder="you@example.in"
            autoComplete="email"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-danger" role="alert">{errors.email.message}</p>
          )}
        </div>

        {/* Mobile */}
        <div>
          <label htmlFor="mobile" className="block text-xs font-600 text-muted-foreground mb-1.5 tracking-wide uppercase">
            Mobile Number <span className="text-danger">*</span>
          </label>
          <div className="flex gap-2">
            <div className="flex items-center px-3 rounded-lg text-sm text-muted-foreground font-500 flex-shrink-0" style={{ background: 'var(--input)', border: '1px solid var(--border)', height: '40px' }}>
              +91
            </div>
            <input
              id="mobile"
              type="tel"
              className="input-field"
              placeholder="98765 43210"
              autoComplete="tel"
              {...register('mobile', {
                required: 'Mobile number is required',
                pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number' },
              })}
            />
          </div>
          {errors.mobile && (
            <p className="mt-1 text-xs text-danger" role="alert">{errors.mobile.message}</p>
          )}
        </div>

        {/* PAN */}
        <div>
          <label htmlFor="pan" className="block text-xs font-600 text-muted-foreground mb-1.5 tracking-wide uppercase">
            PAN Number <span className="text-danger">*</span>
          </label>
          <input
            id="pan"
            type="text"
            className="input-field uppercase"
            placeholder="ABCDE1234F"
            maxLength={10}
            {...register('pan', {
              required: 'PAN number is required',
              pattern: { value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: 'Enter a valid PAN number' },
            })}
          />
          {errors.pan && (
            <p className="mt-1 text-xs text-danger" role="alert">{errors.pan.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="reg-password" className="block text-xs font-600 text-muted-foreground mb-1.5 tracking-wide uppercase">
            Password <span className="text-danger">*</span>
          </label>
          <div className="relative">
            <input
              id="reg-password"
              type={showPassword ? 'text' : 'password'}
              className="input-field pr-10"
              placeholder="Min. 8 characters"
              autoComplete="new-password"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {password && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${(strength.score / 4) * 100}%`, background: strength.color }}
                />
              </div>
              <span className="text-xs font-500" style={{ color: strength.color }}>{strength.label}</span>
            </div>
          )}
          {errors.password && (
            <p className="mt-1 text-xs text-danger" role="alert">{errors.password.message}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" className="block text-xs font-600 text-muted-foreground mb-1.5 tracking-wide uppercase">
            Confirm Password <span className="text-danger">*</span>
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              className="input-field pr-10"
              placeholder="Re-enter your password"
              autoComplete="new-password"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (val) => val === password || 'Passwords do not match',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-xs text-danger" role="alert">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Terms */}
        <div className="space-y-2.5">
          <div className="flex items-start gap-2.5">
            <input
              id="termsAccepted"
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded accent-primary cursor-pointer flex-shrink-0"
              {...register('termsAccepted', { required: 'You must accept the terms' })}
            />
            <label htmlFor="termsAccepted" className="text-xs text-muted-foreground cursor-pointer leading-relaxed">
              I agree to the <span className="text-primary hover:underline cursor-pointer">Terms of Service</span> and <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>
            </label>
          </div>
          {errors.termsAccepted && (
            <p className="text-xs text-danger" role="alert">{errors.termsAccepted.message}</p>
          )}
          <div className="flex items-start gap-2.5">
            <input
              id="riskDisclosureAccepted"
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded accent-primary cursor-pointer flex-shrink-0"
              {...register('riskDisclosureAccepted', { required: 'You must acknowledge the risk disclosure' })}
            />
            <label htmlFor="riskDisclosureAccepted" className="text-xs text-muted-foreground cursor-pointer leading-relaxed">
              I acknowledge the <span className="text-primary hover:underline cursor-pointer">Risk Disclosure</span> — crypto investments can result in loss of capital
            </label>
          </div>
          {errors.riskDisclosureAccepted && (
            <p className="text-xs text-danger" role="alert">{errors.riskDisclosureAccepted.message}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full h-11"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Creating account…
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-primary font-600 hover:underline">
          Sign in
        </button>
      </p>
    </div>
  );
}